import React from "react";
import { useUiPreferences } from "../prefs/useUiPreferences";
import { MAX_PINNED_LINKS } from "./pinnedLinks";
import type { PinnedLink } from "./pinnedLinks";

export type UsePinnedLinksResult = {
  items: PinnedLink[];
  isPinned: (id: string) => boolean;
  /** false = Limit erreicht, es wurde nichts gespeichert. */
  addPin: (pin: PinnedLink) => boolean;
  removePin: (id: string) => void;
  /** false = Limit erreicht (nur beim Hinzufügen möglich). */
  togglePin: (pin: PinnedLink) => boolean;
};

function dedupeById(items: PinnedLink[] | undefined): PinnedLink[] {
  if (!Array.isArray(items)) return [];
  const seen = new Set<string>();
  const out: PinnedLink[] = [];
  for (const item of items) {
    if (!item || typeof item.id !== "string" || !item.id) continue;
    if (seen.has(item.id)) continue;
    seen.add(item.id);
    out.push(item);
  }
  return out;
}

export function usePinnedLinks(): UsePinnedLinksResult {
  const { prefs, patchPrefs } = useUiPreferences();

  const items = React.useMemo(() => dedupeById(prefs.pinnedLinks?.items), [prefs.pinnedLinks?.items]);

  // Der aktuelle Stand als Ref, damit die Callbacks stabil bleiben und ein Klick nie
  // auf einer veralteten Liste rechnet (z. B. zwei Pins kurz hintereinander).
  const itemsRef = React.useRef(items);
  itemsRef.current = items;

  const write = React.useCallback(
    (next: PinnedLink[]) => {
      patchPrefs({ pinnedLinks: { items: next } });
    },
    [patchPrefs]
  );

  const isPinned = React.useCallback((id: string) => itemsRef.current.some((x) => x.id === id), []);

  const addPin = React.useCallback(
    (pin: PinnedLink) => {
      const current = itemsRef.current;
      if (current.some((x) => x.id === pin.id)) return true;
      if (current.length >= MAX_PINNED_LINKS) return false;
      write([...current, pin]);
      return true;
    },
    [write]
  );

  const removePin = React.useCallback(
    (id: string) => {
      const current = itemsRef.current;
      if (!current.some((x) => x.id === id)) return;
      write(current.filter((x) => x.id !== id));
    },
    [write]
  );

  const togglePin = React.useCallback(
    (pin: PinnedLink) => {
      if (itemsRef.current.some((x) => x.id === pin.id)) {
        removePin(pin.id);
        return true;
      }
      return addPin(pin);
    },
    [addPin, removePin]
  );

  return { items, isPinned, addPin, removePin, togglePin };
}
